import React, {ReactElement, useState} from 'react';
import AnimateHeight from 'react-animate-height';

import {Button, IButtonProps} from '../../components/button';
import {Card, CardContent} from '../../components/card';
import {Container} from '../../components/container';
import {Dropdown} from '../../components/dropdown';
import {Feedback, IFeedbackProps} from './../../components/feedback';
import {Filter, IFilterProps} from './../../components/forms/filter';
import {Grid, GridColumn} from '../../components/grid';
import {Heatmap, IHeatmapProps} from '../../components/heatmap';
import {IQuestionProps, Question} from '../../components/question';
import {
    DashboardGeneralCard,
    DashboardOverviewCard,
    isMobileDevice,
    ITemplateGeneralCardProps,
    ITemplateOverviewCardProps,
    ITemplatePlainTextCardProps,
} from '../../components';
import {IPageTitleAndFiltersProps, PageTitleAndFilters} from '../../modules';
import {PlainTextCard} from '../../components/dashboard-plain-text-card';
import {WeeklyRetroCard, IWeeklyRetroCardProps} from '../../components/dashboard-weekly-retro-card';
import {getTooltipButton} from '../../components/button/button';
import {handleScreenshotCapture} from '../../components/screenshotbutton/screenshot';

export type TDashboardExpanded =
    'overview'
    | 'feedback'
    | 'categories'
    | 'company'
    | 'locations'
    | 'heatmap'
    | 'questions'
    | 'length-of-service'
    | 'roles'
    | 'tags'
    | 'no-data'
    | 'mentor-advice'
    | null;

export interface ITemplateDashboardProps {
    pageTitleAndFilters: IPageTitleAndFiltersProps;
    categories?: ITemplateGeneralCardProps;
    locations?: ITemplateGeneralCardProps;
    lengthOfService?: ITemplateGeneralCardProps;
    roles?: ITemplateGeneralCardProps;
    tags?: ITemplateGeneralCardProps;
    defaultExpanded?: TDashboardExpanded;
    company?: ITemplateGeneralCardProps;
    expanded?: TDashboardExpanded;
    feedback?: ITemplateDashboardFeedbackProps;
    heatmap?: ITemplateDashboardHeatmapProps;
    history?: ITemplateOverviewCardProps;
    questions?: ITemplateDashboardQuestionsProps;
    adHocQuestions?: ITemplateDashboardQuestionsProps;
    noData?: ITemplatePlainTextCardProps;
    plainText?: ITemplatePlainTextCardProps;
    weeklyRetro?: IWeeklyRetroCardProps[];
}

export interface ITemplateDashboardFeedbackProps extends ITemplateDashboardCardHeaderProps {
    data: IFeedbackProps;
    includeLikesAndComments?: boolean;
}

export interface ITemplateDashboardCardHeaderProps {
    title: string;
    actions?: IButtonProps[];
    more?: IButtonProps;
    businessUnit?: string
    period?: string
}

export interface ITemplateDashboardHeatmapProps {
    heatmap: IHeatmapProps;
    title: string;
    actions?: IButtonProps[];
    filter?: IFilterProps;
}

export interface ITemplateDashboardQuestionsProps {
    items: IQuestionProps[];
    title?: string;
    button?: IButtonProps;
    infoButton?: IButtonProps;
    actions?: IButtonProps[];
}

const TemplateDashboard = (props: ITemplateDashboardProps): JSX.Element => {
    const [expanded, setExpanded] = useState<TDashboardExpanded>(props.defaultExpanded ? props.defaultExpanded : null);

    const renderDropdownActions: (actions: IButtonProps[]) => JSX.Element = (actions: IButtonProps[]) => (<Dropdown
        placement="bottom"
        triggerType="click"
    >
        <div className="text">
            {actions.map((action: IButtonProps, i: number) => (
                <div key={'history-action-' + i}>
                    <Button {...action} style="link"/>
                </div>
            ))}
        </div>
    </Dropdown>
    );

    const renderActions: (actions: IButtonProps[]) => JSX.Element = (actions: IButtonProps[]) => {
        if (actions.length === 1) {
            return <Button {...actions[0]} color="white" icon="info" style="link"/>;
        }

        return renderDropdownActions(actions);
    };

    const renderFeedbackActions = (actions: IButtonProps[]): ReactElement => (
        <>{actions.map((action) => (
            <GridColumn width={['xs']} key={action.icon}>
                {getTooltipButton(action)}
            </GridColumn>))}</>
    );

    const getExpandedColumn = (name?: TDashboardExpanded): string[] => {
        if (expanded && name === expanded) {
            return ['lg-12'];
        } else if (expanded) {
            return ['0'];
        } else if (name === 'heatmap') {
            return ['lg-12'];
        } else if (name === 'no-data') {
            return ['lg-12'];
        } else if (name === 'questions') {
            return ['lg-12'];
        } else {
            return ['lg-6'];
        }
    };

    const getAnimatedHeight = (name?: TDashboardExpanded): string | number => {
        if (expanded && name !== expanded) {
            return 0;
        } else {
            return 'auto';
        }
    };

    const renderFeedbackCardHeader = (headerProps: ITemplateDashboardCardHeaderProps, name?: TDashboardExpanded): JSX.Element => (
        <Grid noWrap={['sm']} align={['between-xs']}>
            <GridColumn width={['xs-8', 'min-xs']}>
                <h3>{headerProps.title}</h3>
            </GridColumn>
            {(headerProps.actions || headerProps.more) &&
                <GridColumn width={['xs-4', 'min-xs']}>
                    <Grid align={['middle-xs']} justify={['end-xs']}>
                        {headerProps.actions &&
                            renderFeedbackActions(headerProps.actions)
                        }
                        {headerProps.more && name &&
                            <GridColumn width={['xs']}>
                                {!isMobileDevice() &&
                                <Button
                                    icon={'print'} className={'button__noFill'} color={'white'}
                                    onClick={(): void => {
                                        setExpanded(expanded === name ? null : name);
                                        setTimeout(() => {
                                            handleScreenshotCapture(name?? undefined);
                                        }, 500);
                                    }}
                                />
                                }
                                <Button
                                    {...headerProps.more}
                                    color="white"
                                    icon={expanded === name ? 'collapse' : 'expand'}
                                    onClick={(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
                                        event.preventDefault();
                                        setExpanded(expanded === name ? null : name);
                                        if (headerProps.more && headerProps.more.onClick) {
                                            headerProps.more.onClick(event);
                                        }
                                    }}
                                    style="link"
                                />
                            </GridColumn>
                        }
                    </Grid>
                </GridColumn>
            }
        </Grid>
    );

    const renderOverviewCard = (history: ITemplateOverviewCardProps, name: TDashboardExpanded): JSX.Element => (
        <GridColumn width={getExpandedColumn(name)}>
            <Card className={`card_${name}`}>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight(name)}>
                    <DashboardOverviewCard
                        {...history}
                        isExpanded={expanded === name}
                        setExpanded={(expandedValue): void => setExpanded(expandedValue ? name : null)}
                        name={name}
                    />
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    const renderFeedbackCard = (feedback: ITemplateDashboardFeedbackProps, name: TDashboardExpanded): JSX.Element => (
        <GridColumn width={getExpandedColumn(name)}>
            <Card className={`card_${name}`}>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight(name)}>
                    <CardContent>
                        <div className="text">
                            {renderFeedbackCardHeader(feedback, name)}
                            <Feedback {...feedback.data} aspectRatio={expanded !== name} includeLikesAndComments={feedback.includeLikesAndComments}/>
                        </div>
                    </CardContent>
                </AnimateHeight>
            </Card>
        </GridColumn>
    );


    const renderGeneralCard = (cardProps: ITemplateGeneralCardProps, name: TDashboardExpanded): JSX.Element => (
        <GridColumn width={getExpandedColumn(name)}>
            <Card className={`card_${name}`}>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight(name)}>
                    <DashboardGeneralCard
                        {...cardProps}
                        isExpanded={expanded === name}
                        setExpanded={(expandedValue): void => setExpanded(expandedValue ? name : null)}
                        name={name}
                    />
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    const renderHeatmapCard = (cardProps: ITemplateDashboardHeatmapProps): JSX.Element => (
        <GridColumn width={getExpandedColumn('heatmap')}>
            <Card>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight()}>
                    <CardContent>
                        <div className="text text--double">
                            <Grid wrap={['xs']} noWrap={['sm']} align={['middle-xs']}>
                                <GridColumn>
                                    <Grid noWrap={['xs']} align={['middle-xs']}>
                                        <GridColumn width={['max-xs', 'max-sm']}>
                                            <h3>{cardProps.title}</h3>
                                        </GridColumn>
                                        {cardProps.actions &&
                                            <GridColumn width={['min-xs', 'min-sm']}>
                                                {renderActions(cardProps.actions)}
                                            </GridColumn>
                                        }
                                    </Grid>
                                    {cardProps.filter &&
                                        <Grid justify={['end-xs', 'end-sm']}>
                                            <GridColumn width={['min-xs', 'sm']}>
                                                <Filter {...cardProps.filter} hideLabel={true}/>
                                            </GridColumn>
                                        </Grid>
                                    }
                                </GridColumn>
                            </Grid>
                            <Heatmap {...cardProps.heatmap} />
                        </div>
                    </CardContent>
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    const renderPlainTextCard = (cardProps: ITemplatePlainTextCardProps, name: TDashboardExpanded): JSX.Element => (
        <GridColumn width={getExpandedColumn(name)}>
            <Card borderColor={name === 'mentor-advice' ? 'green' : 'grey'}>
                <AnimateHeight className="h-animate-height" height={getAnimatedHeight()}>
                    <PlainTextCard text={cardProps.text}/>
                </AnimateHeight>
            </Card>
        </GridColumn>
    );

    const renderWeeklyRetroCard = (cardProps: IWeeklyRetroCardProps[]): JSX.Element[] => cardProps.map((card, index) => (
        <GridColumn width={getExpandedColumn('feedback')} key={`weekly-retro-${index}`}><Card className="card_weekly-retro"><WeeklyRetroCard {...card}/></Card></GridColumn>
    ));

    const renderQuestionsTitle = (questions: ITemplateDashboardQuestionsProps | undefined): JSX.Element => (
        <GridColumn width={getExpandedColumn('questions')}>
            <AnimateHeight className="h-animate-height" height={getAnimatedHeight()}>
                <div className="text text--double">
                    {questions && questions.title && questions && questions.actions &&
                        <h3>{questions.title} {renderActions(questions.actions)}</h3>}
                </div>
            </AnimateHeight>
        </GridColumn>
    );

    const renderQuestions = (questions: ITemplateDashboardQuestionsProps): JSX.Element => (
        <GridColumn>
            <AnimateHeight className="h-animate-height" height={getAnimatedHeight()}>
                <div className="text text--double">
                    <Grid equalHeight={true} wrap={['xs']}>
                        {questions.items.map((el: IQuestionProps, i: number) => (
                            <GridColumn
                                width={['xs-12', 'lg-6', 'sm-6','xl-4']} key={i} className="question-grid-col">
                                <Question {...el} />
                            </GridColumn>
                        ))}
                    </Grid>
                    {questions.button &&
                        <Grid align={['center-xs']}>
                            <GridColumn width={['xs']}>
                                <Button {...questions.button} color="white"/>
                            </GridColumn>
                        </Grid>
                    }
                </div>
            </AnimateHeight>
        </GridColumn>
    );

    return (
        <Container>
            <div className="text text--triple">
                <PageTitleAndFilters {...props.pageTitleAndFilters}/>
                <Grid wrap={['xs']} equalHeight={true} transition={true}>
                    {props.history && renderOverviewCard(props.history, 'overview')}
                    {props.weeklyRetro && renderWeeklyRetroCard(props.weeklyRetro)}
                    {props.feedback && renderFeedbackCard(props.feedback, 'feedback')}
                    {props.categories && renderGeneralCard(props.categories, 'categories')}
                    {props.company && renderGeneralCard(props.company, 'company')}
                    {props.locations && renderGeneralCard(props.locations, 'locations')}
                    {props.lengthOfService && renderGeneralCard(props.lengthOfService, 'length-of-service')}
                    {props.roles && renderGeneralCard(props.roles, 'roles')}
                    {props.tags && renderGeneralCard(props.tags, 'tags')}
                    {props.plainText && renderPlainTextCard(props.plainText, 'mentor-advice')}
                    {props.heatmap && renderHeatmapCard(props.heatmap)}
                    {props.questions || props.adHocQuestions ? renderQuestionsTitle(props.questions || props.adHocQuestions) : undefined}
                    {props.adHocQuestions && renderQuestions(props.adHocQuestions)}
                    {props.questions && renderQuestions(props.questions)}
                    {props.noData && renderPlainTextCard(props.noData, 'no-data')}
                </Grid>
            </div>
        </Container>
    );
};

export default TemplateDashboard;
